import { useCallback, useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { inputCls } from "../constants";
import { Field } from "./JobOverview";

const SQFT = [["1000", "0 - 1,000 sq.ft"], ["3000", "1,001 - 3,000 sq.ft"], ["5000", "3,001 - 5,000 sq.ft"], ["10000", "5,001 - 10,000+ sq.ft"]];
const sqftValue = (n) => {
  if (!n || n <= 1000) return "1000";
  if (n <= 3000) return "3000";
  if (n <= 5000) return "5000";
  return "10000";
};

export function JobServices({ job, photographers = [], onChanged }) {
  const [catalog, setCatalog] = useState(null);
  const [services, setServices] = useState(job.services || []);
  const [sqft, setSqft] = useState(sqftValue(job.sqft));
  const [vs, setVs] = useState(job.virtual_staging?.option || "none");
  const [tw, setTw] = useState(job.twilight_option || "none");
  const [coupon, setCoupon] = useState(job.quote?.coupon?.code || "");
  const [assignments, setAssignments] = useState(job.service_assignments || {});
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(job.quote);
  const loadCatalog = useCallback(async () => {
    try { const response = await api.get("/services"); setCatalog(response.data); }
    catch (error) { console.error("Unable to load the service catalog", error); toast.error("Could not load services"); }
  }, []);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);
  useEffect(() => {
    let active = true;
    if (!services.length && vs === "none" && tw === "none") { setEstimate(null); return; }
    api.post("/pricing/quote", { services, sqft: Number(sqft), virtual_staging: vs === "none" ? null : { option: vs }, twilight_option: tw === "none" ? null : tw, coupon_code: coupon.trim() || null }).then((response) => { if (active) setEstimate(response.data); }).catch((error) => { if (active) console.error("Unable to refresh the service estimate", error); });
    return () => { active = false; };
  }, [services, sqft, vs, tw, coupon]);

  const toggle = (id) => setServices((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const assignService = (serviceId, photographerId) => setAssignments((current) => { const next = { ...current }; if (photographerId === "auto") delete next[serviceId]; else next[serviceId] = photographerId; return next; });
  const save = async () => {
    setSaving(true);
    try {
      const prunedAssignments = Object.fromEntries(Object.entries(assignments).filter(([serviceId]) => services.includes(serviceId)));
      await api.patch(`/admin/jobs/${job.id}`, {
        services, sqft: parseInt(sqft, 10), virtual_staging: vs === "none" ? {} : { option: vs },
        twilight_option: tw === "none" ? "" : tw, coupon_code: coupon.trim(), service_assignments: prunedAssignments,
      });
      setAssignments(prunedAssignments);
      toast.success("Services updated — total recalculated"); onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!catalog) return null;
  const selectedServices = catalog.services.filter((s) => services.includes(s.id));
  return (
    <div className="grid lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-6">
        <div>
          <div className="eyebrow mb-3">Services</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {catalog.services.map((s) => {
              const on = services.includes(s.id);
              return (
                <button key={s.id} data-testid={`job-service-${s.id}`} onClick={() => toggle(s.id)}
                  className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors ${on ? "border-[#0C2347] bg-[#0C2347] text-white" : "border-[#E4E9F0] hover:border-[#0C2347]/40"}`}>
                  <span className={`h-5 w-5 rounded-md flex items-center justify-center border ${on ? "border-white" : "border-[#E4E9F0]"}`}>{on && <Check className="h-3 w-3" />}</span>
                  <span className="flex-1">{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>
        {selectedServices.length > 0 && (
          <div>
            <div className="eyebrow mb-3">Service assignments</div>
            <div className="space-y-2">
              {selectedServices.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#E4E9F0] px-4 py-2.5">
                  <span className="text-sm text-[#101828]">{s.name}</span>
                  <Select value={assignments[s.id] || "auto"} onValueChange={(value) => assignService(s.id, value)}>
                    <SelectTrigger data-testid={`job-service-shooter-${s.id}`} className="w-48 h-9 rounded-lg border-[#E4E9F0] text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Same as order shooter</SelectItem>
                      {photographers.filter((p) => p.active).map((p) => <SelectItem key={p.id} value={p.id} data-testid={`job-service-shooter-option-${s.id}-${p.id}`}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Square footage">
            <Select value={sqft} onValueChange={setSqft}><SelectTrigger data-testid="job-sqft" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent>{SQFT.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Coupon code"><Input data-testid="job-coupon" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="None" className={inputCls} /></Field>
          <Field label="Virtual staging">
            <Select value={vs} onValueChange={setVs}><SelectTrigger data-testid="job-vs" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{catalog.virtual_staging.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Dusk / Twilight">
            <Select value={tw} onValueChange={setTw}><SelectTrigger data-testid="job-tw" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{catalog.twilight_options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
        <Button data-testid="job-services-save" disabled={saving} onClick={save} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white px-8">{saving ? "Saving…" : "Save & recalculate"}</Button>
      </div>
      <div className="rounded-xl border border-[#E4E9F0] bg-white p-5 h-fit" style={{ boxShadow: "0 3px 12px rgba(15,23,42,0.035)" }}>
        <div className="eyebrow mb-3">Current order</div>
        <div className="divide-y divide-[#E4E9F0] text-sm">
          {estimate?.line_items?.map((li) => <div key={li.id} className="py-2 flex justify-between gap-3"><span>{li.name}</span><span className="font-mono">${li.amount}</span></div>)}
        </div>
        {estimate?.discount > 0 && <div className="pt-2 text-sm flex justify-between text-emerald-700"><span>Discount {estimate.coupon?.code}</span><span className="font-mono">−${estimate.discount}</span></div>}
        <div className="border-t border-[#E4E9F0] mt-3 pt-3 flex justify-between"><span className="eyebrow">Live estimated total</span><span data-testid="live-order-total-price" className="font-serif-display text-2xl text-[#101828]">${estimate?.total ?? 0}</span></div>
      </div>
    </div>
  );
}
